import {
  IDateRange,
  IDate,
  InventoryEventTypeEnum,
  ProductTypeEnum,
} from './types';
import request from '@/utils/request';
import {
  sleep,
  dateToIDate,
  isIDateInIDateRange,
  stringFromIDate,
  randomString,
  isSameMonth,
  isLastMonth,
  formatDate,
  stringToIDate,
} from '@/utils/index';
import { calculateTicketBlockAmount } from '@/utils/product';
import {
  mockProductSummariesCache,
  list_product_summaries,
  mockProductsCount,
} from './product';
import { update_booking } from './booking';
import { mockAgentSummariesCache } from './agent';

// data type
export namespace search_inventory_events {
  export interface Request {
    dateRange?: IDateRange;
  }

  export interface SourceDestination {
    name?: string;
    agentId?: string;
  }

  export interface InventoryEvent {
    eventId: string;
    date: IDate;
    sourceDest: SourceDestination;
    type: InventoryEventTypeEnum;
  }

  export interface Response {
    events: InventoryEvent[];
  }
}

export namespace get_inventory_event_detail {
  export interface Request {
    eventId: string;
  }

  export interface SourceDestination {
    name?: string;
    agentId?: string;
  }

  export interface Product {
    productId: string;
    ticketBlocks: TicketBlock[];
    amount: number;
  }

  export interface TicketBlock {
    productId: string;
    ticketNumberStart: string;
    ticketNumberEnd: string;
    amount: number;
  }

  export interface Response {
    id: string;
    type: InventoryEventTypeEnum;
    date: IDate;
    sourceDest: SourceDestination;
    products: Product[];
    traceNumber?: string;
    traceUrl?: string;
    signedBy: string;
    note?: string;
    signedAt: string;
    bookingId?: string;
    version?: string;
  }
}

export namespace incoming_inventory_event {
  export interface TicketBlock {
    ticketNumberStart: string;
    ticketNumberEnd: string;
  }

  export interface Product {
    productId: string;
    ticketBlocks: TicketBlock[];
  }

  export interface Source {
    type: Source.SourceType;
    value: string;
  }

  export namespace Source {
    export enum SourceType {
      FREE_TEXT = 'FREE_TEXT',
      AGENT_ID = 'AGENT_ID',
    }
  }

  export interface Request {
    signedBy: string;
    source: Source;
    products: Product[];
    type: IncomingInventoryEventType;
    date: IDate;
    note?: string;
  }

  export interface Response {
    eventId: string;
  }

  export enum IncomingInventoryEventType {
    ARRIVAL = 'ARRIVAL',
    RETURN = 'RETURN',
  }
}

export namespace outgoing_inventory_event {
  export interface TicketBlock {
    ticketNumberStart: string;
    ticketNumberEnd: string;
  }

  export interface Product {
    productId: string;
    ticketBlocks: TicketBlock[];
  }

  export interface Destination {
    type: DestinationType;
    value: string;
  }

  export interface Request {
    signedBy: string;
    destination: Destination;
    traceNumber?: string;
    traceUrl?: string;
    products: Product[];
    date: IDate;
    note?: string;
  }

  export interface Response {
    eventId: string;
  }

  export enum DestinationType {
    FREE_TEXT = 'FREE_TEXT',
    AGENT_ID = 'AGENT_ID',
  }
}

export namespace get_monthly_inventory_report {
  export interface Request {
    month: IDate;
  }

  export interface TicketBlock {
    ticketNumberStart: string;
    ticketNumberEnd: string;
    amount: number;
  }

  export interface InventoryDetail {
    date: IDate;
    id?: string;
    bookingId?: string;
    partnerName: string;
    isReturned?: boolean;
    ticketBlocks: TicketBlock[];
    totalAmount: number;
  }

  export interface Report {
    productId: string;
    productName: string;
    carryOverAmount: number;
    incomingAmount: number;
    outgoingAmount: number;
    incomingReturnedAmount: number;
    netIncomingAmount: number;
    netOutgoingAmount: number;
    stockAmount: number;
    currency: string;
    listPrice: number;
    totalStockPrice: number;
    carryOverDetails: InventoryDetail[];
    incomingDetails: InventoryDetail[];
    outgoingDetails: InventoryDetail[];
    csvBlob: string;
  }

  export interface Response {
    month: IDate;
    reports: Report[];
    approvalDocuments: Response.ApprovalDocument[];
  }

  export namespace Response {
    export interface ApprovalDocument {
      fiscalYear: number;
      url: string;
      csvBlob: string;
    }
  }
}

// mock
const useMock = process.env.VUE_APP_API_USE_MOCK === 'true';

const mockIDate = (start: Date, end: Date) => {
  const diffMs = end.getTime() - start.getTime();
  const diffDates = diffMs / 1000 / 3600 / 24;
  const date = new Date(start);
  date.setDate(date.getDate() + Math.round(Math.random() * diffDates));
  return dateToIDate(date);
};

const mockInventoryEventId = () => randomString(4);

const mockInventoryEventTypeEnum = (hash: number) => {
  const num = hash >= 0 ? hash : -hash;
  return num % 9 === 0
    ? InventoryEventTypeEnum.INCOMING_RETURN
    : (num % 9) % 2 === 0
    ? InventoryEventTypeEnum.INCOMING_ARRIVAL
    : InventoryEventTypeEnum.OUTGOING;
};

const mockInventoryEventsCount = 100;

const mockInitialInventoryEventsCache = () => {
  const events: search_inventory_events.InventoryEvent[] = [];
  const today = new Date();
  const twoMonthsAgo = new Date();
  twoMonthsAgo.setDate(twoMonthsAgo.getDate() - 60);
  for (let i = 0; i < mockInventoryEventsCount; i++) {
    const event = {
      eventId: mockInventoryEventId(),
      date: mockIDate(twoMonthsAgo, today),
      sourceDest: {
        name: i % 2 === 0 ? `dummyName${i}` : null,
        agentId:
          i % 2 === 1
            ? Array.from(mockAgentSummariesCache.values())[i].id
            : null,
      } as search_inventory_events.SourceDestination,
      type: mockInventoryEventTypeEnum(i),
    } as search_inventory_events.InventoryEvent;
    events.push(event);
  }
  return new Map(events.map(e => [e.eventId, e]));
};
const mockInventoryEventsCache = mockInitialInventoryEventsCache();

const mockInitialInventoryEventDetailsCache = (
  events: Map<string, search_inventory_events.InventoryEvent>
) => {
  const details: get_inventory_event_detail.Response[] = [];
  const realTickets = Array.from(mockProductSummariesCache.values()).filter(
    p => (p.type as ProductTypeEnum) === ProductTypeEnum.REAL_TICKET
  );
  const step = 5;
  let i = mockInventoryEventsCount;
  events.forEach(e => {
    const product = realTickets[i % realTickets.length];
    const detail = {
      id: e.eventId,
      type: e.type,
      date: e.date,
      sourceDest: {
        name: e.sourceDest.name,
        agentId: e.sourceDest.agentId,
      },
      products: [
        {
          productId: product.id,
          ticketBlocks: [
            {
              productId: product.id,
              ticketNumberStart: `${i * step}`.padStart(5, '0'),
              ticketNumberEnd: `${i * step + step}`.padStart(5, '0'),
            },
          ],
        },
      ],
      signedBy: `dummySignedBy${i}`,
      note: `dummuyNote${i}`,
      signedAt: new Date(stringFromIDate(e.date) as string).toLocaleString(),
      version: '0',
    } as get_inventory_event_detail.Response;
    if (
      (detail.type as InventoryEventTypeEnum) ===
      InventoryEventTypeEnum.OUTGOING
    ) {
      detail.traceNumber = `dummyTraceNumber${i}`;
      detail.traceUrl = `dummy.trace.url/${i}`;
    }
    details.push(detail);
    i--;
  });
  return new Map(details.map(d => [d.id, d]));
};
export const mockInventoryEventDetailsCache = mockInitialInventoryEventDetailsCache(
  mockInventoryEventsCache
);

const mockSearchInventoryEvents = (req: search_inventory_events.Request) => {
  const events: search_inventory_events.InventoryEvent[] = [];
  mockInventoryEventsCache.forEach(e => {
    if (isIDateInIDateRange(e.date, req.dateRange || null)) {
      events.push(e);
    }
  });
  return {
    events: events.sort(
      (a, b) =>
        new Date(stringFromIDate(b.date) as string).getTime() -
        new Date(stringFromIDate(a.date) as string).getTime()
    ),
  } as search_inventory_events.Response;
};

const mockGetInventoryEventDetail = (
  req: get_inventory_event_detail.Request
) => {
  return mockInventoryEventDetailsCache.get(
    req.eventId
  ) as get_inventory_event_detail.Response;
};

const sourceToSourceDest = (source: incoming_inventory_event.Source) => {
  const result = {} as {
    name?: string;
    agentId?: string;
  };
  if (
    (source.type as incoming_inventory_event.Source.SourceType) ===
    incoming_inventory_event.Source.SourceType.FREE_TEXT
  ) {
    result.name = source.value;
  } else if (
    (source.type as incoming_inventory_event.Source.SourceType) ===
    incoming_inventory_event.Source.SourceType.AGENT_ID
  ) {
    result.agentId = source.value;
  }
  return result;
};

const mockIncomingInventoryEvent = (req: incoming_inventory_event.Request) => {
  const eventId = mockInventoryEventId();

  mockInventoryEventsCache.set(eventId, {
    eventId: eventId,
    date: req.date,
    sourceDest: sourceToSourceDest(req.source),
    type:
      (req.type as incoming_inventory_event.IncomingInventoryEventType) ===
      incoming_inventory_event.IncomingInventoryEventType.ARRIVAL
        ? InventoryEventTypeEnum.INCOMING_ARRIVAL
        : (req.type as incoming_inventory_event.IncomingInventoryEventType) ===
          incoming_inventory_event.IncomingInventoryEventType.RETURN
        ? InventoryEventTypeEnum.INCOMING_RETURN
        : InventoryEventTypeEnum.UNKNOWN_EVENT,
  });

  mockInventoryEventDetailsCache.set(eventId, {
    id: eventId,
    date: req.date,
    sourceDest: sourceToSourceDest(req.source),
    type:
      (req.type as incoming_inventory_event.IncomingInventoryEventType) ===
      incoming_inventory_event.IncomingInventoryEventType.ARRIVAL
        ? InventoryEventTypeEnum.INCOMING_ARRIVAL
        : (req.type as incoming_inventory_event.IncomingInventoryEventType) ===
          incoming_inventory_event.IncomingInventoryEventType.RETURN
        ? InventoryEventTypeEnum.INCOMING_RETURN
        : InventoryEventTypeEnum.UNKNOWN_EVENT,
    products: req.products.map(p => {
      const ticketBlocks: get_inventory_event_detail.TicketBlock[] = [];
      let amount = 0;
      p.ticketBlocks.forEach(b => {
        const blockAmount = calculateTicketBlockAmount(
          b.ticketNumberStart,
          b.ticketNumberEnd
        );
        amount += blockAmount;
        ticketBlocks.push({
          productId: p.productId,
          ticketNumberStart: b.ticketNumberStart,
          ticketNumberEnd: b.ticketNumberEnd,
          amount: blockAmount,
        });
      });
      return {
        productId: p.productId,
        ticketBlocks: ticketBlocks,
        amount: amount,
      } as get_inventory_event_detail.Product;
    }),
    signedBy: req.signedBy,
    signedAt: new Date().toLocaleString(),
    note: req.note,
    version: '0',
  });

  return {
    eventId: eventId,
  } as incoming_inventory_event.Response;
};

const destinationToSourceDest = (
  destination: outgoing_inventory_event.Destination
) => {
  const result = {} as {
    name?: string;
    agentId?: string;
  };
  if (
    (destination.type as outgoing_inventory_event.DestinationType) ===
    outgoing_inventory_event.DestinationType.FREE_TEXT
  ) {
    result.name = destination.value;
  } else if (
    (destination.type as outgoing_inventory_event.DestinationType) ===
    outgoing_inventory_event.DestinationType.AGENT_ID
  ) {
    result.agentId = destination.value;
  }
  return result;
};

const mockOutgoingInventoryEvent = (req: outgoing_inventory_event.Request) => {
  const eventId = mockInventoryEventId();

  mockInventoryEventsCache.set(eventId, {
    eventId: eventId,
    date: req.date,
    sourceDest: destinationToSourceDest(req.destination),
    type: InventoryEventTypeEnum.OUTGOING,
  });

  mockInventoryEventDetailsCache.set(eventId, {
    id: eventId,
    date: req.date,
    sourceDest: destinationToSourceDest(req.destination),
    type: InventoryEventTypeEnum.OUTGOING,
    products: req.products.map(p => {
      const ticketBlocks: get_inventory_event_detail.TicketBlock[] = [];
      let amount = 0;
      p.ticketBlocks.forEach(b => {
        const blockAmount = calculateTicketBlockAmount(
          b.ticketNumberStart,
          b.ticketNumberEnd
        );
        amount += blockAmount;
        ticketBlocks.push({
          productId: p.productId,
          ticketNumberStart: b.ticketNumberStart,
          ticketNumberEnd: b.ticketNumberEnd,
          amount: blockAmount,
        });
      });
      return {
        productId: p.productId,
        ticketBlocks: ticketBlocks,
        amount: amount,
      } as get_inventory_event_detail.Product;
    }),
    signedBy: req.signedBy,
    signedAt: new Date().toLocaleString(),
    traceNumber: req.traceNumber,
    traceUrl: req.traceUrl,
    note: req.note,
    version: '0',
  });

  return {
    eventId: eventId,
  } as incoming_inventory_event.Response;
};

const mockMonthlyInventoryReportInventoryDetails = (
  event: get_inventory_event_detail.Response,
  product: get_inventory_event_detail.Product
) => {
  const detail = {
    date: stringToIDate(formatDate(new Date(event.signedAt))),
    partnerName: event.sourceDest.agentId || event.sourceDest.name,
    isReturned:
      (event.type as InventoryEventTypeEnum) ===
      InventoryEventTypeEnum.INCOMING_RETURN
        ? true
        : undefined,
    ticketBlocks: [],
    totalAmount: 0,
  } as get_monthly_inventory_report.InventoryDetail;
  product.ticketBlocks.forEach(t => {
    const amount = mockGetTicketBlockAmount(
      t.ticketNumberStart,
      t.ticketNumberEnd
    );
    detail.ticketBlocks.push({
      ticketNumberStart: t.ticketNumberStart,
      ticketNumberEnd: t.ticketNumberEnd,
      amount: amount,
    });
    detail.totalAmount += amount;
  });

  return detail;
};

const mockGetTicketBlockAmount = (
  ticketNumberStart: string,
  ticketNumberEnd: string
) => {
  return calculateTicketBlockAmount(ticketNumberStart, ticketNumberEnd);
};

const mockInitialMonthlyInventoryReport = (
  product: list_product_summaries.Product
) => {
  return {
    productId: product.id,
    productName: product.name,
    carryOverAmount: 0,
    incomingAmount: 0,
    outgoingAmount: 0,
    incomingReturnedAmount: 0,
    netIncomingAmount: 0,
    netOutgoingAmount: 0,
    stockAmount: 0,
    currency: product.price.currency,
    listPrice: product.price.listPrice,
    totalStockPrice: 0,
    carryOverDetails: [],
    incomingDetails: [],
    outgoingDetails: [],
    csvBlob: '',
  } as get_monthly_inventory_report.Report;
};

const mockFillInMonthlyInventoryReport = (
  requestedMonth: IDate,
  reports: Map<string, get_monthly_inventory_report.Report>,
  reportsLastMonth: Map<string, get_monthly_inventory_report.Report>,
  products: Map<string, list_product_summaries.Product>,
  isTargetMonth: (eventMonth: IDate, requestedMonth: IDate) => boolean
) => {
  mockInventoryEventDetailsCache.forEach(i => {
    if (isTargetMonth(i.date, requestedMonth)) {
      i.products.forEach(p => {
        p.ticketBlocks.forEach(t => {
          const product = products.get(t.productId)!;
          // initialize report if not existed
          if (!reports.has(t.productId)) {
            reports.set(
              t.productId,
              mockInitialMonthlyInventoryReport(product)
            );
          }
          const report = reports.get(t.productId)!;
          const amount = mockGetTicketBlockAmount(
            t.ticketNumberStart,
            t.ticketNumberEnd
          );
          // fill in amount and detail
          if (
            (i.type as InventoryEventTypeEnum) ===
            InventoryEventTypeEnum.INCOMING_ARRIVAL
          ) {
            report.incomingAmount += amount;
            report.incomingDetails.push(
              mockMonthlyInventoryReportInventoryDetails(i, p)
            );
          } else if (
            (i.type as InventoryEventTypeEnum) ===
            InventoryEventTypeEnum.INCOMING_RETURN
          ) {
            report.incomingAmount += amount;
            report.incomingReturnedAmount += amount;
            report.incomingDetails.push(
              mockMonthlyInventoryReportInventoryDetails(i, p)
            );
          } else if (
            (i.type as InventoryEventTypeEnum) ===
            InventoryEventTypeEnum.OUTGOING
          ) {
            report.outgoingAmount += amount;
            report.outgoingDetails.push(
              mockMonthlyInventoryReportInventoryDetails(i, p)
            );
          }
        });
      });
    }
  });
  // calculate net amount and price
  reports.forEach(r => {
    r.netIncomingAmount = r.incomingAmount - r.incomingReturnedAmount;
    r.netOutgoingAmount = r.outgoingAmount - r.incomingReturnedAmount;
    const reportLastMonth = reportsLastMonth.get(r.productId);
    if (reportLastMonth) {
      r.carryOverAmount += reportLastMonth.stockAmount;
    }
    r.stockAmount = r.carryOverAmount + r.incomingAmount - r.outgoingAmount;
    r.totalStockPrice = r.stockAmount * r.listPrice;
    // dummy carryOverDetails
    const lastMonth = new Date(stringFromIDate(requestedMonth)!);
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    r.carryOverDetails.push({
      date: stringToIDate(formatDate(lastMonth))!,
      partnerName: 'partnerName',
      ticketBlocks: [
        {
          ticketNumberStart: 'dummyStart',
          ticketNumberEnd: 'dummyEnd',
          amount: 2,
        },
      ],
      totalAmount: 2,
    });
  });
};

const mockGetMonthlyInventoryReport = (
  req: get_monthly_inventory_report.Request
) => {
  const products = mockProductSummariesCache;
  // fill in report of last month, assume carry out is 0
  const reportsLastMonth: Map<
    string,
    get_monthly_inventory_report.Report
  > = new Map();
  mockFillInMonthlyInventoryReport(
    req.month,
    reportsLastMonth,
    new Map(),
    products,
    isLastMonth
  );
  // fill in report of requested month, use report of last month for carry out
  const reports: Map<string, get_monthly_inventory_report.Report> = new Map();
  mockFillInMonthlyInventoryReport(
    req.month,
    reports,
    reportsLastMonth,
    products,
    isSameMonth
  );

  return {
    month: req.month,
    reports: Array.from(reports.values()),
    approvalDocuments: [
      {
        fiscalYear: req.month.year,
        url:
          'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        csvBlob: 'not implemented',
      },
    ],
  } as get_monthly_inventory_report.Response;
};

export const mockInternalBookingInventoryEventFromBooking = (
  req: update_booking.Request,
  bookingId: string,
  agentId: string
) => {
  const now = new Date();
  const id = mockInventoryEventId();
  const detail: get_inventory_event_detail.Response = {
    id: id,
    type: InventoryEventTypeEnum.OUTGOING,
    date: stringToIDate(formatDate(now))!,
    sourceDest: {
      agentId: agentId,
    },
    products: [],
    traceNumber: `dummyTraceNumber${id}`,
    traceUrl: `dummy.trace.url/${id}`,
    signedBy: `dummySignedBy${id}`,
    note: `dummuyNote${id}`,
    signedAt: now.toLocaleString(),
    bookingId: bookingId,
    version: '0',
  };
  req.recordTicketNumbers!.orders.forEach(o => {
    o.products.forEach(p => {
      p.ticketBlocks.forEach(b => {
        let product = detail.products.find(
          product => product.productId === p.productId
        );
        if (!product) {
          product = {
            productId: p.productId,
            ticketBlocks: [],
            amount: 0,
          };
          detail.products.push(product);
        }
        const blockAmont = calculateTicketBlockAmount(
          b.ticketNumberFrom,
          b.ticketNumberTo
        );
        product.ticketBlocks.push({
          productId: p.productId,
          ticketNumberStart: b.ticketNumberFrom,
          ticketNumberEnd: b.ticketNumberTo,
          amount: blockAmont,
        });
        product.amount += blockAmont;
      });
    });
  });

  mockInventoryEventsCache.set(detail.id, {
    eventId: detail.id,
    date: detail.date,
    sourceDest: detail.sourceDest,
    type: detail.type,
  });
  mockInventoryEventDetailsCache.set(detail.id, detail);
};

// api
export const searchInventoryEvents = async (
  req: search_inventory_events.Request
) => {
  if (useMock) {
    await sleep(500);
    return mockSearchInventoryEvents(req);
  }

  const res = await request({
    url: '/v1/search-inventory-events',
    method: 'post',
    data: req,
  });
  return res.data as search_inventory_events.Response;
};

export const getInventoryEventDetail = async (
  req: get_inventory_event_detail.Request
) => {
  if (useMock) {
    await sleep(500);
    return mockGetInventoryEventDetail(req);
  }

  const res = await request({
    url: '/v1/get-inventory-event-detail',
    method: 'post',
    data: req,
  });
  return res.data as get_inventory_event_detail.Response;
};

export const incomingInventoryEvent = async (
  req: incoming_inventory_event.Request
) => {
  if (useMock) {
    await sleep(500);
    return mockIncomingInventoryEvent(req);
  }

  const res = await request({
    url: '/v1/incoming-inventory-event',
    method: 'post',
    data: req,
  });
  return res.data as incoming_inventory_event.Response;
};

export const outgoingInventoryEvent = async (
  req: outgoing_inventory_event.Request
) => {
  if (useMock) {
    await sleep(500);
    return mockOutgoingInventoryEvent(req);
  }

  const res = await request({
    url: '/v1/outgoing-inventory-event',
    method: 'post',
    data: req,
  });
  return res.data as outgoing_inventory_event.Response;
};

export const getMonthlyInventoryReport = async (
  req: get_monthly_inventory_report.Request
) => {
  if (useMock) {
    await sleep(300);
    return mockGetMonthlyInventoryReport(req);
  }

  const res = await request({
    url: '/v1/get-monthly-inventory-report',
    method: 'post',
    data: req,
  });
  return res.data as get_monthly_inventory_report.Response;
};
